import debounce from "lodash.debounce";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { fetchUsers } from "../../store/ticket/actions";

const CommonSelect = ({
  value,
  role,
  onChange,
  placeholder = "Select an option...",
  isClearable = true,
  isSearchable = true,
  isDisabled = false,
}) => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.ticketReducer);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Ensure user options contain strings
  const toTitleCase = (str) =>
    str
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  
  const userOptions = users
    ?.filter((user) => user?.firstName)
    .map((user) => {
      const fullName = toTitleCase(`${user.firstName} ${user.lastName}`);
      const label =
        user.roleId == "6"
          ? `${fullName} (${user.email})`
          : fullName;
  
      return {
        value: String(user.id),
        label,
      };
    });
  
  


  // Ensure the initial value is in correct format
  const getFormattedValue = (inputValue) => {
    if (
      typeof inputValue === "object" &&
      inputValue?.value &&
      inputValue?.label
    ) {
      return inputValue;
    }
    const matchedUser = userOptions.find(
      (option) => option.value === String(inputValue)
    );
    return matchedUser || null;
  };

  const [selectedOption, setSelectedOption] = useState(
    getFormattedValue(value)
  );

  const handleUserSelect = (selectedOption) => {
    setSelectedOption(selectedOption);
    onChange(selectedOption);
  };

  // Load initial users when component mounts
  useEffect(() => {
    setLoading(true);
    dispatch(fetchUsers("", role)).finally(() => setLoading(false));
  }, [dispatch, role]);

  // Filter options locally based on input
  const filteredOptions = useMemo(() => {
    if (!inputValue || inputValue.trim() === "") {
      return userOptions;
    }
    
    const searchTerm = inputValue.toLowerCase();
    return userOptions.filter((option) => 
      option.label.toLowerCase().includes(searchTerm)
    );
  }, [userOptions, inputValue]);

  const fetchUsersDebounced = useMemo(
    () =>
      debounce((searchValue, roleValue) => {
        setLoading(true);
        dispatch(fetchUsers(searchValue, roleValue)).finally(() => setLoading(false));
      }, 500),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      fetchUsersDebounced.cancel();
    };
  }, [fetchUsersDebounced]);

  return (
    <Select
      options={filteredOptions}
      value={selectedOption}
      onChange={handleUserSelect}
      inputValue={inputValue}
      onInputChange={(newValue) => {
        setInputValue(newValue);
        if (newValue.trim().length >= 3) {
          fetchUsersDebounced(newValue, role);
        }
      }}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      isLoading={loading}
      placeholder={placeholder}
      isClearable={isClearable}
      filterOption={null} // Disable default filtering as we're handling it ourselves
      styles={{
        control: (base) => ({
          ...base,
          borderColor: "#ccc",
          boxShadow: "none",
          "&:hover": { borderColor: "#aaa" },
        }),
      }}
    />
  );
};

CommonSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  role: PropTypes.string,
};

export default CommonSelect;